/**
 * Stage 00 — Tokens section for the Atomic Design showcase.
 * Covers colour (brand + semantic), typography, spacing, leave-type tokens,
 * and the unified calendar day-cell vocabulary.
 */

import { Grid2, PrimitiveCard, SectionHeader, Swatch, SwatchGrid } from './shared'

export function TokensSection() {
  return (
    <section id="tokens" style={{ marginBottom: 80, scrollMarginTop: 24 }}>
      <SectionHeader
        num="00"
        name="Tokens"
        tag="Foundation"
        stage="atoms"
        desc="Design tokens are the single source of truth for every visual value — color, type, spacing, and radius. All primitives reference tokens; nothing is hardcoded."
      />

      <Grid2>
        {/* Color · Brand */}
        <PrimitiveCard
          name="Color · Brand"
          tag="Token"
          desc="Steel Blue is primary, Pear is the ILMU / AI accent, Space Cadet anchors interactive surfaces."
        >
          <SwatchGrid>
            <Swatch color="#0082CB" name="Steel Blue" hex="#0082CB" />
            <Swatch color="#97CAEB" name="Baby Blue" hex="#97CAEB" />
            <Swatch color="#CDDE00" name="Pear (AI)" hex="#CDDE00" />
            <Swatch color="#202945" name="Space Cadet" hex="#202945" />
            <Swatch color="#5B6670" name="Payne" hex="#5B6670" />
            <Swatch color="#D8D6DF" name="French Gray" hex="#D8D6DF" />
          </SwatchGrid>
        </PrimitiveCard>

        {/* Color · Semantic */}
        <PrimitiveCard
          name="Color · Semantic"
          tag="Token"
          desc="Status feedback uses fixed mappings — drift here breaks user trust."
        >
          <SwatchGrid>
            <Swatch color="#2D9C6E" name="Success" hex="#2D9C6E" />
            <Swatch color="#C28A2D" name="Warning" hex="#C28A2D" />
            <Swatch color="#C4445A" name="Danger" hex="#C4445A" />
            <Swatch color="#0082CB" name="Info" hex="#0082CB" />
          </SwatchGrid>
        </PrimitiveCard>

        {/* Typography */}
        <PrimitiveCard
          name="Typography"
          tag="Token"
          desc="Geist for UI, Geist Mono for data, code, and meta. Tracking gets tighter as size grows."
          col
        >
          {[
            { label: 'Aa Display', size: 32, weight: 600, ls: '-0.02em', spec: '32 / 600' },
            { label: 'H1 · Page title', size: 24, weight: 600, ls: '-0.02em', spec: '24 / 600' },
            { label: 'H2 · Section', size: 18, weight: 600, ls: '-0.015em', spec: '18 / 600' },
            { label: 'Body — the default text size.', size: 14, weight: 400, ls: undefined, spec: '14 / 400' },
            { label: 'Caption / helper', size: 12.5, weight: 500, ls: undefined, spec: '12.5 / 500', muted: true },
          ].map(({ label, size, weight, ls, spec, muted }) => (
            <div
              key={spec}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                padding: '10px 0',
                borderBottom: '1px dashed var(--border)',
              }}
            >
              <span
                style={{
                  fontSize: size,
                  fontWeight: weight,
                  letterSpacing: ls,
                  color: muted ? 'var(--text-2)' : undefined,
                }}
              >
                {label}
              </span>
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10.5,
                  color: 'var(--text-3)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                {spec}
              </span>
            </div>
          ))}
        </PrimitiveCard>

        {/* Spacing & radius */}
        <PrimitiveCard
          name="Spacing &amp; radius"
          tag="Token"
          desc="Spacing scales by 4px; radii from 6 (controls) to 20 (modals)."
          col
        >
          {[4, 8, 12, 16, 24].map((px, i) => (
            <div
              key={px}
              style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '6px 0' }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  color: 'var(--text-2)',
                  width: 60,
                }}
              >
                space-{i + 1}
              </span>
              <span
                style={{
                  height: 18,
                  background: 'var(--baby-blue-tint)',
                  borderRadius: 3,
                  width: px,
                }}
              />
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10.5,
                  color: 'var(--text-3)',
                  marginLeft: 'auto',
                }}
              >
                {px} px
              </span>
            </div>
          ))}
          <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10.5,
                color: 'var(--text-3)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Radius
            </span>
            {[6, 10, 14, 20].map((r) => (
              <span key={r} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span
                  style={{
                    width: 30,
                    height: 24,
                    background: 'var(--baby-blue-tint)',
                    border: '1px solid var(--steel-blue)',
                    borderRadius: r,
                    display: 'inline-block',
                  }}
                />
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10.5,
                    color: 'var(--text-2)',
                  }}
                >
                  {r}
                </span>
              </span>
            ))}
          </div>
        </PrimitiveCard>

        {/* Leave types */}
        <PrimitiveCard
          name="Leave types"
          tag="Token"
          desc="Each leave type has a fixed colour used in the day-cell, badge, chip, and balance tile. 11 types — only 9 are user-selectable; emergency and unpaid are auto-assigned."
        >
          <SwatchGrid>
            {[
              { color: '#0082CB', name: 'Annual', hex: '§ 3.1 · #0082CB' },
              { color: '#5B6670', name: 'Sick', hex: '§ 3.2 · #5B6670' },
              { color: '#9F2940', name: 'Hospitalization', hex: '§ 3.2.1 · #9F2940' },
              { color: '#7C8590', name: 'Compassionate', hex: '§ 3.3 · #7C8590' },
              { color: '#005A8E', name: 'Maternity', hex: '§ 3.4 · #005A8E' },
              { color: '#97CAEB', name: 'Paternity', hex: '§ 3.5 · #97CAEB' },
              { color: '#202945', name: 'Marriage', hex: '§ 3.6 · #202945' },
              { color: '#CDDE00', name: 'Birthday', hex: '§ 3.7 · #CDDE00' },
              { color: '#9CA4AD', name: 'Replacement', hex: '§ 3.8 · #9CA4AD' },
              { color: '#C28A2D', name: 'Unpaid · auto', hex: '§ 3.9 · #C28A2D' },
              { color: '#CDDE00', name: 'Emergency · auto', hex: '§ 6.2 · #CDDE00' },
            ].map((s) => (
              <Swatch key={s.name} color={s.color} name={s.name} hex={s.hex} />
            ))}
          </SwatchGrid>
        </PrimitiveCard>

        {/* Calendar states */}
        <PrimitiveCard
          name="Calendar states"
          tag="Token"
          desc="Unified day-cell vocabulary used on every calendar in the app. Holidays are french gray, blackouts are an inset warning ring, today gets a navy dot."
          col
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              gap: 4,
              width: '100%',
            }}
          >
            {[
              { n: 3 }, { n: 4, cls: 'weekend' }, { n: 5, cls: 'today' }, { n: 6 },
              { n: 7, cls: 'holiday' }, { n: 8, cls: 'blackout' }, { n: 9, cls: 'high-absence' },
              { n: 12, cls: 'endpoint range-start' }, { n: 13, cls: 'in-range' },
              { n: 14, cls: 'in-range' }, { n: 15, cls: 'in-range' },
              { n: 16, cls: 'in-range' }, { n: 17, cls: 'endpoint range-end' }, { n: 18, cls: 'weekend' },
            ].map((d, i) => (
              <div key={i} className={`dcell${d.cls ? ' ' + d.cls : ''}`}>
                {d.n}
              </div>
            ))}
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px 14px',
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontWeight: 600,
              color: 'var(--text-2)',
            }}
          >
            {[
              {
                label: 'Today',
                dot: (
                  <span
                    style={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      background: 'var(--space-cadet)',
                      display: 'inline-block',
                    }}
                  />
                ),
              },
              { label: 'Weekend' },
              { label: 'Holiday', swatch: { bg: 'var(--french-gray-2)', border: '1px solid var(--french-gray)' } },
              { label: 'Blackout', swatch: { boxShadow: 'inset 0 0 0 1.5px var(--warning)' } },
              { label: 'High absence', swatch: { bg: 'rgba(194,138,45,.22)' } },
              { label: 'In range', swatch: { bg: 'var(--baby-blue-tint)' } },
              { label: 'Endpoint', swatch: { bg: 'var(--space-cadet)' } },
            ].map(({ label, dot, swatch }) => (
              <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span
                  style={{
                    width: 11,
                    height: 11,
                    borderRadius: 3,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: swatch?.bg ?? 'transparent',
                    border: swatch?.border,
                    boxShadow: swatch?.boxShadow,
                  }}
                >
                  {dot}
                </span>
                {label}
              </span>
            ))}
          </div>
        </PrimitiveCard>
      </Grid2>
    </section>
  )
}
